<template>
    <div v-if="activeSlide" class="slide-container" :class="mouseActive ? 'mouse-active' : ''" ref="slideshowContainer">
        <div class="slide-image-wrapper-outter" @mousemove="handleMouseMove">
            <div class="slide-image-wrapper">
                <transition name="slide-fade" mode="out-in">
                    <video
                        class="slide-image"
                        v-if="activeSlide.mediaType === tributeMediaTypeEnum.VIDEO"
                        ref="videoElement"
                        :src="activeSlide.url"
                        muted
                        :key="activeSlideIndex + '-vid'"
                        @ended="onVideoEnded"
                        @canplay="onVideoCanPlay"
                        @click="togglePlayPause"
                    ></video>

                    <v-img
                        v-else
                        contain
                        class="slide-image"
                        :src="activeSlide.url"
                        :key="activeSlideIndex + '-img'"
                        alt=""
                        @click="togglePlayPause"
                    />
                </transition>

                <div class="slide-controls">
                    <div class="bar-section">
                        <div class="control-icon control-btn pointer" @click="prevSlide()">
                            <font-awesome-icon icon="fa-solid fa-chevron-left"></font-awesome-icon>
                        </div>
                        <div class="control-icon control-btn pointer" @click="togglePlayPause">
                            <font-awesome-icon v-if="isPlaying" icon="fa-solid fa-pause"></font-awesome-icon>
                            <font-awesome-icon v-else icon="fa-solid fa-play"></font-awesome-icon>
                        </div>

                        <div class="control-icon control-btn pointer" @click="nextSlide()">
                            <font-awesome-icon icon="fa-solid fa-chevron-right"></font-awesome-icon>
                        </div>

                        <v-menu
                            ref="settingsMenu"
                            top
                            offset-y
                            nudge-top="10px"
                            :close-on-content-click="false"
                            @input="onMenuToggle"
                            attach=""
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <div class="control-icon control-btn pointer" v-bind="attrs" v-on="on">
                                    <font-awesome-icon icon="fa-solid fa-gear"></font-awesome-icon>
                                </div>
                            </template>
                            <v-list>
                                <v-list-item>
                                    <v-text-field
                                        style="width: 100px"
                                        type="number"
                                        v-model="tempSlideDuration"
                                        label="Slide Duration"
                                        step="0.1"
                                        :min="minSlideDuration"
                                        suffix="s"
                                    ></v-text-field>
                                </v-list-item>
                                <v-list-item>
                                    <div class="d-flex flex-column">
                                        <span class="text-caption">Images Only</span>
                                        <div>
                                            <v-switch class="m-0" v-model="removeVideos" dense hide-details></v-switch>
                                        </div>
                                    </div>
                                </v-list-item>
                                <v-list-item>
                                    <v-btn color="#ff530d" dark small depressed @click="saveSettings">Save</v-btn>
                                </v-list-item>
                            </v-list>
                        </v-menu>

                        <div class="control-icon control-btn pointer" @click="toggleFullscreen">
                            <font-awesome-icon v-if="fullscreen" icon="fa-solid fa-compress"></font-awesome-icon>
                            <font-awesome-icon v-else icon="fa-solid fa-expand"></font-awesome-icon>
                        </div>
                    </div>

                    <div class="bar-section flex-grow-1">
                        <v-slider
                            class="mx-2 pointer"
                            v-model="slideCurrentTime"
                            :max="slideDuration"
                            @end="handleScrubEnd"
                            @start="handleScrubStart"
                            hide-details
                            color="#ff530d"
                            step="0.05"
                            track-color="#d8d8d8"
                        ></v-slider>

                        <div>{{ slideCurrentTime | formatTimeStamp }} / {{ slideDuration | formatTimeStamp }}</div>
                    </div>
                    <div class="bar-section">
                        <div class="control-icon">{{ activeSlideIndex + 1 }} / {{ filteredSlides.length }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { tributeMediaTypeEnum } from '@/constants';
import moment from 'moment';
export default {
    data() {
        return {
            tributeMediaTypeEnum,
            token: '',
            apiService: null,
            activeSlideIndex: 0,
            fullscreen: false,
            isPlaying: false,
            defaultIntervalSeconds: 8,
            slideDurationSetting: 8,
            tempSlideDuration: 8,
            removeVideos: false,
            mouseActive: false,
            mouseTimer: null,
            settingsMenuOpen: false,
            slideCurrentTime: 0,
            slideStartTime: null,
            progressTimer: null,
            minSlideDuration: 5,
        };
    },
    props: {
        slides: {
            type: Array,
            default: () => [],
        },
        autoPlay: {
            type: Boolean,
            default: false,
        },
        intervalSeconds: {
            type: Number,
            default: 8,
        },
    },
    computed: {
        activeSlide() {
            if (!this.filteredSlides || this.activeSlideIndex >= this.filteredSlides.length) {
                return null;
            }

            return this.filteredSlides[this.activeSlideIndex];
        },
        videoSlideActive() {
            if (this.activeSlide && this.activeSlide.mediaType === this.tributeMediaTypeEnum.VIDEO) {
                return true;
            }
            return false;
        },
        filteredSlides() {
            let slides = this.slides;
            if (this.slides?.length && this.removeVideos) {
                slides = slides.filter(s => s.mediaType !== tributeMediaTypeEnum.VIDEO);
            }

            return slides;
        },
        slideDuration() {
            if (this.videoSlideActive && this.activeSlide?.duration) {
                return this.activeSlide.duration;
            }

            return this.slideDurationSetting ?? this.defaultIntervalSeconds;
        },
    },
    filters: {
        formatTimeStamp(seconds) {
            const duration = moment.duration(seconds, 'seconds');
            const minutes = duration.minutes();
            const secs = duration.seconds();

            const formattedMinutes = String(minutes).padStart(2, '0');
            const formattedSeconds = String(secs).padStart(2, '0');

            return `${formattedMinutes}:${formattedSeconds}`;
        },
    },
    methods: {
        saveSettings() {
            if (this.tempSlideDuration < this.minSlideDuration) {
                this.tempSlideDuration = this.minSlideDuration;
            }

            this.tempSlideDuration = Math.round(this.tempSlideDuration * 10) / 10;
            this.slideDurationSetting = this.tempSlideDuration;

            console.log(this.slideDurationSetting, 'slide setting');

            this.closeMenu();
            this.$emit('slide-duration', this.slideDurationSetting);
        },
        async onVideoCanPlay() {
            if (this.isPlaying && this.videoSlideActive) {
                this.playVideoSlide();
            }
        },
        startProgressTracking() {
            this.stopProgressTracking();

            this.isPlaying = true;

            this.progressTimer = setInterval(() => {
                this.slideCurrentTime = Math.min(this.slideCurrentTime + 0.1, this.slideDuration);

                if (!this.videoSlideActive && this.slideCurrentTime >= this.slideDuration) {
                    this.nextSlide();
                }
            }, 100);
        },
        setVideoTime(time) {
            const videoEl = this.$refs.videoElement;
            if (videoEl) {
                try {
                    videoEl.currentTime = time;
                } catch (err) {
                    console.log(err, 'video play error');
                }
            }
        },
        handleScrubEnd() {
            if (this.videoSlideActive) {
                this.setVideoTime(this.slideCurrentTime);
            }
        },
        handleScrubStart() {
            if (this.videoSlideActive) {
                this.pauseVideoSlide();
            }
        },
        async stopProgressTracking() {
            if (this.progressTimer) {
                clearInterval(this.progressTimer);
                this.progressTimer = null;
            }
        },

        handleMouseMove() {
            this.mouseActive = true;

            this.showControls = true;

            clearTimeout(this.mouseTimer);

            this.mouseTimer = setTimeout(() => {
                this.clearMouseActive();
            }, 2000);
        },
        clearMouseActive() {
            if (this.settingsMenuOpen) return;

            this.mouseActive = false;
            clearTimeout(this.mouseTimer);

            this.closeMenu();
        },
        closeMenu() {
            const menuEl = this.$refs.settingsMenu;
            if (menuEl) {
                menuEl?.save();
            }
        },
        onMenuToggle(evt) {
            this.settingsMenuOpen = evt;
            this.slideDurationSetting = this.tempSlideDuration;

            if (!this.settingsMenuOpen) {
                this.saveSettings();
            }
        },
        onVideoEnded() {
            if (this.isPlaying) {
                this.nextSlide();
            }
        },
        resetSlideshow() {
            this.stopSlideshow();
            this.slideCurrentTime = 0;
            this.activeSlideIndex = 0;
        },
        playSlideShow() {
            if (!this.filteredSlides || this.filteredSlides.length <= 1) return;

            this.startProgressTracking();
            this.playVideoSlide();
            this.isPlaying = true;
        },
        stopSlideshow() {
            this.stopProgressTracking();
            this.pauseVideoSlide();
            this.isPlaying = false;
        },
        playVideoSlide() {
            const videoEl = this.$refs.videoElement;
            if (videoEl) {
                try {
                    videoEl.play();
                    this.startProgressTracking();
                } catch (err) {
                    console.log(err, 'video play error');
                }
            }
        },
        pauseVideoSlide() {
            this.stopProgressTracking();
            const videoEl = this.$refs.videoElement;
            if (videoEl && this.videoSlideActive) {
                try {
                    videoEl.pause();
                } catch (err) {
                    console.log(err, 'video play error');
                }
            }
        },
        togglePlayPause() {
            if (this.isPlaying) {
                this.stopSlideshow();
            } else {
                this.playSlideShow();
            }
        },
        nextSlide(stopSlideshow = false) {
            if (this.activeSlideIndex >= this.filteredSlides.length - 1) {
                this.activeSlideIndex = 0;
            } else {
                this.activeSlideIndex++;
            }

            if (stopSlideshow) {
                this.stopSlideshow();
            }
        },
        prevSlide(stopSlideshow = false) {
            if (this.activeSlideIndex <= 0) {
                this.activeSlideIndex = this.filteredSlides.length - 1;
            } else {
                this.activeSlideIndex--;
            }

            if (stopSlideshow) {
                this.stopSlideshow();
            }
        },

        toggleFullscreen() {
            const previewEl = this.$refs.slideshowContainer;

            if (!previewEl) return;

            if (!document.fullscreenElement) {
                this.enterFullscreen(previewEl);
                this.fullscreen = true;
            } else {
                this.exitFullscreen();
                this.fullscreen = false;
            }
        },
        enterFullscreen(element) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                // Safari
                element.webkitRequestFullscreen();
            } else if (element.mozRequestFullScreen) {
                // Firefox
                element.mozRequestFullScreen();
            } else if (element.msRequestFullscreen) {
                // IE/Edge
                element.msRequestFullscreen();
            }
        },

        exitFullscreen() {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                // Safari
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                // Firefox
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                // IE/Edge
                document.msExitFullscreen();
            }
        },
        handleFullscreenChange() {
            const fullscreenElement =
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement;

            this.fullscreen = fullscreenElement === this.$refs.slideshowContainer;
        },
        initFullscreenListeners() {
            document.addEventListener('fullscreenchange', this.handleFullscreenChange);
            document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange); // Safari
            document.addEventListener('mozfullscreenchange', this.handleFullscreenChange); // Firefox
            document.addEventListener('MSFullscreenChange', this.handleFullscreenChange); // IE/Edge
        },
        removeFullscreenListeners() {
            document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
        },
    },
    watch: {
        slides: {
            handler(newSlides) {
                if (newSlides && newSlides.length > 1 && this.autoPlay) {
                    this.resetSlideshow();

                    if (this.autoPlay) {
                        this.playSlideShow();
                    }
                }
            },
            immediate: true,
        },
        intervalSeconds(newVal) {
            let seconds = newVal > 0 ? newVal : 8;

            this.slideDurationSetting = seconds;
            this.tempSlideDuration = seconds;
        },
        removeVideos(newVal) {
            if (this.videoSlideActive) {
                this.activeSlideIndex = 0;
            }
        },
        activeSlideIndex(newVal) {
            this.stopProgressTracking();
            this.slideCurrentTime = 0;

            if (this.isPlaying) {
                this.startProgressTracking();
            }
        },
    },
    async mounted() {
        this.initFullscreenListeners();
    },
    beforeDestroy() {
        this.removeFullscreenListeners();
        this.stopSlideshow();
    },
};
</script>
<style lang="scss">
.slide-container {
    max-width: 100%;
    height: 100%;
    overflow: hidden;
    width: 100%;

    &.mouse-active .slide-controls {
        opacity: 1 !important;
        transition-delay: 0ms !important;
    }

    .slide-fade-enter-active,
    .slide-fade-leave-active {
        transition: opacity 0.8s ease-in-out;
    }

    .slide-fade-enter,
    .slide-fade-leave-to {
        opacity: 0;
    }

    .slide-image-wrapper-outter {
        // border: 2px solid red;
        display: flex;
        align-items: center;
        justify-content: center;
        max-width: 100%;
        max-height: 100%;
        height: 100%;
    }
    .slide-image-wrapper {
        display: flex;
        // border: 5px solid lime;
        justify-content: center;
        width: fit-content;
        position: relative;
        min-width: 100%;
        background-color: black;
        flex-shrink: 1;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
    }

    .slide-image {
        // border: 5px solid pink;
        display: block;
        max-width: 100%;
        max-height: 100%;
        overflow: hidden;
        object-fit: contain;
    }

    .slide-controls {
        position: absolute;
        color: white;
        left: 0;
        right: 0;
        bottom: 0;
        margin: auto;
        z-index: 2;
        display: flex;
        padding: 8px 12px;
        // border: 2px solid red;
        justify-content: space-between;
        opacity: 0;
        transition: opacity 400ms ease;
        transition-delay: 800ms;
    }

    .control-icon {
        font-size: 1.2rem;
        padding: 6px 10px;
    }

    .bar-section {
        gap: 8px;

        display: flex;
        align-items: center;
    }

    .control-btn {
        border-radius: 4px;

        // border: 1px solid red;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .control-btn:hover {
        background-color: rgba(255, 255, 255, 0.25);
    }
}
</style>
